import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";

export type DSBadgeVariant =
  | "brand"
  | "green"
  | "yellow"
  | "red"
  | "blue"
  | "cyan"
  | "orange"
  | "neutral";
export type DSBadgeSize = "sm" | "md";

export interface DSBadgeProps {
  children: React.ReactNode;
  variant?: DSBadgeVariant;
  size?: DSBadgeSize;
  /** Optional leading icon */
  icon?: IconProp;
  dataTestId: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * DSBadge - Non-interactive status display component
 *
 * Use for read-only status indicators, labels, and tags that don't require user interaction.
 * For interactive chips (filters, selectable tags), use DSChip instead.
 */
export const DSBadge = ({
  children,
  variant = "brand",
  size = "md",
  icon,
  dataTestId,
  ref,
}: DSBadgeProps) => {
  // Base styles
  const baseStyles =
    "inline-flex items-center gap-1 rounded-full border border-transparent";

  // Size styles
  const sizeStyles = {
    sm: "h-6 px-sm text-xs",
    md: "h-8 px-md text-sm",
  };

  // Variant styles - subtle backgrounds for status display
  const variantStyles = {
    brand: "bg-brand-subtle text-brand",
    green: "bg-success-subtle text-success",
    yellow: "bg-warning-subtle text-warning",
    red: "bg-danger-subtle text-danger",
    blue: "bg-blue-100 text-blue-500 dark:bg-blue-900 dark:text-blue-400",
    cyan: "bg-cyan-100 text-cyan-500 dark:bg-cyan-900 dark:text-cyan-400",
    orange:
      "bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-400",
    neutral: "bg-tertiary text-secondary",
  };

  return (
    <div
      ref={ref}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
      data-variant={variant}
      className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]}`}
    >
      {icon && <FontAwesomeIcon icon={icon} />}
      <span className="font-body font-regular whitespace-nowrap">
        {children}
      </span>
    </div>
  );
};

DSBadge.displayName = "DSBadge";
